/**
 * Shared BNPL (Buy Now Pay Later) payment method factory.
 * Used by Klarna, Afterpay, and Affirm payment methods.
 */

import type { PaymentKitErrors, PaymentKitStates, TInternalFuncs } from "../types";
import { collectFraudMetadata, definePaymentMethod, getOrCreateCheckoutRequestId } from "../utils";

export type BnplCustomerInfo = {
  first_name: string;
  last_name: string;
  email?: string;
};

export type BnplStartRequest = {
  processor_id: string;
  customer_info: BnplCustomerInfo;
  fraud_metadata: {
    ipAddress?: string;
    browserInfo?: { [key: string]: unknown };
    processorFraudInfo?: { [key: string]: unknown };
  };
  setup_future_usage?: boolean;
};

export type BnplStartResponse = {
  approval_url: string;
  popup_token: string;
  checkout_attempt_id: string;
};

export type BnplStatusResponse = {
  completed: boolean;
  status: "success" | "failed" | "cancelled";
  result?: unknown;
  error?: string;
};

export type BnplCheckoutResponse = {
  id: string;
  checkoutAttemptId: string;
  checkoutSessionId: string;
  state: string;
  customerId?: string;
  paymentMethodId?: string;
  processorUsed?: string;
  subscriptionId?: string;
  invoiceId?: string;
  invoiceNumber?: number;
};

export type BnplSubmitOptions = {
  processorId: string;
  customerInfo: BnplCustomerInfo;
  setupFutureUsage?: boolean;
};

type BnplStates = PaymentKitStates & {
  popup?: Window | null;
  pollInterval?: ReturnType<typeof setInterval>;
};

export type BnplProviderConfig = {
  name: string;
  endpointPath: string;
  supportsSetupFutureUsage?: boolean;
};

const createSubmitPayment = (states: BnplStates, config: BnplProviderConfig) => {
  const { name, endpointPath, supportsSetupFutureUsage } = config;
  const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);

  const submitPayment: TInternalFuncs["submitPayment"] = async (_fields, options) => {
    const { apiBaseUrl, secureToken, environment } = states;
    const bnplOptions = options as BnplSubmitOptions;

    if (!bnplOptions?.processorId) {
      return { errors: { processor_id: "Processor ID is required" } as PaymentKitErrors };
    }

    if (!bnplOptions?.customerInfo?.first_name || !bnplOptions?.customerInfo?.last_name) {
      return {
        errors: {
          customer_name: "Customer first and last name are required",
        } as PaymentKitErrors,
      };
    }

    try {
      const checkoutRequestId = getOrCreateCheckoutRequestId(environment);

      const requestBody: BnplStartRequest = {
        processor_id: bnplOptions.processorId,
        customer_info: bnplOptions.customerInfo,
        fraud_metadata: collectFraudMetadata(),
      };

      if (supportsSetupFutureUsage && bnplOptions.setupFutureUsage !== undefined) {
        requestBody.setup_future_usage = bnplOptions.setupFutureUsage;
      }

      const startResponse = await fetch(`${apiBaseUrl}/api/checkout/${secureToken}/stripe/${endpointPath}/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-request-id": checkoutRequestId,
        },
        body: JSON.stringify(requestBody),
      });

      if (!startResponse.ok) {
        let errorMessage = `Failed to start ${capitalizedName} checkout (${startResponse.status})`;
        try {
          const errorData = await startResponse.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          errorMessage = startResponse.statusText || errorMessage;
        }
        return {
          errors: {
            [name]: errorMessage,
          } as PaymentKitErrors,
        };
      }

      const { approval_url, popup_token, checkout_attempt_id } = (await startResponse.json()) as BnplStartResponse;

      const popup = window.open(approval_url, capitalizedName, "width=600,height=700");

      if (!popup) {
        return {
          errors: {
            [name]: `Failed to open ${capitalizedName} popup. Please allow popups for this site.`,
          } as PaymentKitErrors,
        };
      }

      states.popup = popup;

      return new Promise((resolve) => {
        let popupClosedGracePolls = 0;
        const MAX_GRACE_POLLS = 5;

        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await fetch(
              `${apiBaseUrl}/api/checkout/${secureToken}/popup/status?popup_token=${popup_token}`,
            );

            if (!statusResponse.ok) {
              clearInterval(pollInterval);
              popup.close();
              resolve({
                errors: {
                  [name]: `Failed to check ${capitalizedName} status`,
                } as PaymentKitErrors,
              });
              return;
            }

            const statusResult = (await statusResponse.json()) as BnplStatusResponse;

            if (statusResult.completed) {
              clearInterval(pollInterval);
              popup.close();

              if (statusResult.status === "success") {
                // Result is returned directly from popup status endpoint
                const result = statusResult.result || {
                  id: checkout_attempt_id,
                  checkoutAttemptId: checkout_attempt_id,
                  checkoutSessionId: secureToken,
                  state: "checkout_succeeded",
                };

                resolve({ data: result });
              } else {
                resolve({
                  errors: {
                    [name]: statusResult.error || `${capitalizedName} checkout ${statusResult.status}`,
                  } as PaymentKitErrors,
                });
              }
              return;
            }

            if (popup.closed) {
              popupClosedGracePolls++;
              if (popupClosedGracePolls >= MAX_GRACE_POLLS) {
                clearInterval(pollInterval);
                resolve({
                  errors: {
                    [name]: `${capitalizedName} popup closed by user`,
                  } as PaymentKitErrors,
                });
              }
              return;
            }
          } catch (error) {
            clearInterval(pollInterval);
            popup.close();
            resolve({
              errors: {
                [name]: `Polling error: ${error}`,
              } as PaymentKitErrors,
            });
          }
        }, 2000);

        states.pollInterval = pollInterval;
      });
    } catch (error) {
      return {
        errors: {
          [name]: `${capitalizedName} checkout error: ${error}`,
        } as PaymentKitErrors,
      };
    }
  };

  return submitPayment;
};

export function createBnplPaymentMethod(config: BnplProviderConfig) {
  return definePaymentMethod((paymentKitStates) => {
    const localStates: BnplStates = { ...paymentKitStates };

    return {
      name: config.name,
      externalFuncs: {},
      internalFuncs: {
        submitPayment: createSubmitPayment(localStates, config),
        cleanup: () => {
          if (localStates.pollInterval) {
            clearInterval(localStates.pollInterval);
          }
          if (localStates.popup && !localStates.popup.closed) {
            localStates.popup.close();
          }
        },
      },
    };
  });
}
